<template>
  <div class="inline-block relative" v-bind="$attrs">
    <button
      class="flex rounded-full items-center justify-center ring-1 ring-brand-primary bg-brand-bgdivider-20"
      :class="{
        'size-40': size === 'lg',
        'size-100u': !size || size === 'md',
      }"
      type="button"
      :disabled="isUploading"
      :aria-label="
        isUploading
          ? $t('ui.form.imageUpload.uploadingImage')
          : $t('ui.form.imageUpload.addImage')
      "
      @click="onClick"
    >
      <span
        v-if="isUploading"
        class="loading loading-spinner loading-lg"
        aria-hidden
      />
      <Icon
        v-else-if="model === null"
        icon="mdi:picture"
        class="text-2xl text-basics-secondary-80"
        aria-hidden
      />
      <img
        v-else
        :src="model"
        class="w-full h-full object-cover rounded-full"
        alt=""
        aria-hidden
      />
    </button>

    <div class="absolute top-0 right-0">
      <button
        v-if="model !== null"
        type="button"
        class="btn-ghost p-2 rounded-full absolute translate-center"
        @click="model = null"
      >
        <Icon
          icon="mdi:trash"
          class="text-2xl text-basics-secondary-80"
          aria-hidden
        />
      </button>
    </div>
  </div>

  <input
    ref="fileInputRef"
    type="file"
    accept="image/png, image/gif, image/jpeg"
    style="display: none"
    @change="onFileChange"
  />
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";
import { useAwaitAction } from "@/composables/use-await-action";
import {
  UploadState,
  useAppManagedUpload,
} from "@/composables/use-app-managed-upload";
import { useToastStore } from "@/stores/toast.store";
import { Icon } from "@iconify/vue";
import { useI18n } from "vue-i18n";

const model = defineModel<string | null>({
  default: null,
});

defineProps<{
  size: "md" | "lg";
}>();

const { t } = useI18n();

const toastStore = useToastStore();
const fileInputRef = useTemplateRef("fileInputRef");

const { uploadContent: uploadContent } = useAppManagedUpload();

const state = ref<UploadState>(
  model.value === null ? UploadState.EMPTY : UploadState.UPLOADED,
);

const { handler: onFileChange, loading: isUploading } = useAwaitAction(
  async () => {
    const file = fileInputRef.value?.files?.[0];
    if (!file) return;
    try {
      state.value = UploadState.UPLOADING;
      const url = await uploadContent(file);
      model.value = url;
      state.value = UploadState.UPLOADED;
    } catch (error) {
      console.error(error);
      toastStore.addToast({
        message: t("ui.form.imageUpload.failedToUploadImage"),
        type: "error",
      });
    } finally {
      state.value = UploadState.UPLOADED;
    }
  },
);

function onClick() {
  fileInputRef.value?.click();
}
</script>
